"use client";

import { useEffect, useState } from "react";
import Link from "next/link";

const API = process.env.NEXT_PUBLIC_MO_YOUTUBE_FEED_API || "https://mo-youtube-feed.dng.workers.dev";

interface VehicleCluster {
  make: string;
  model: string;
  channel_count: number;
  start_date: string;
  end_date: string;
  video_count: number;
}

const MAKE_DOMAINS: Record<string, string> = {
  Honda: "honda.com", Acura: "acura.com", Toyota: "toyota.com",
  Lexus: "lexus.com", Mazda: "mazda.com", Subaru: "subaru.com",
  Nissan: "nissanusa.com", BMW: "bmw.com", "Mercedes-Benz": "mercedes-benz.com",
  Audi: "audi.com", Porsche: "porsche.com", Volkswagen: "volkswagen.com",
  Ford: "ford.com", Chevrolet: "chevrolet.com", Hyundai: "hyundai.com",
  Kia: "kia.com", Genesis: "genesis.com", Volvo: "volvocars.com",
  Jaguar: "jaguarusa.com", "Land Rover": "landroverusa.com",
  Ferrari: "ferrari.com", Lamborghini: "lamborghini.com",
  "Alfa Romeo": "alfaromeo.com", Fiat: "fiat.com", Maserati: "maserati.com",
  Peugeot: "peugeot.com", Renault: "renault.com", Citroën: "citroen.com",
};

function makeLogoUrl(make: string): string | null {
  const domain = MAKE_DOMAINS[make];
  if (!domain) return null;
  return `https://www.google.com/s2/favicons?domain=${domain}&sz=32`;
}

export function VideoTeaser() {
  const [clusters, setClusters] = useState<VehicleCluster[]>([]);

  useEffect(() => {
    fetch(`${API}/clusters`)
      .then((r) => r.json())
      .then((data) => { if (Array.isArray(data)) setClusters(data); })
      .catch(() => {});
  }, []);

  // Deduplicate same make+model across weeks
  const deduped = clusters.reduce<VehicleCluster[]>((acc, c) => {
    const existing = acc.find((a) => a.make === c.make && a.model === c.model);
    if (existing) {
      existing.video_count += c.video_count;
      existing.channel_count = Math.max(existing.channel_count, c.channel_count);
    } else {
      acc.push({ ...c });
    }
    return acc;
  }, []);

  if (deduped.length === 0) return null;

  return (
    <div
      className="rounded-xl border border-border-soft bg-card/60 shadow-sm ring-1 ring-border-hair px-5 py-6"
      style={{
        fontFamily: "var(--font-zalando-sans-narrow)",
        borderTop: "2px solid transparent",
        borderImage: "linear-gradient(90deg, #f97316, #ec4899, #8b5cf6) 1",
        borderImageSlice: "1 0 0 0",
      }}
    >
      <p className="text-lg text-fg-3 mb-3" style={{ fontFamily: "var(--font-zalando-sans)", fontWeight: 200 }}>
        Trending on{" "}
        <span
          className="bg-clip-text text-transparent"
          style={{ backgroundImage: "linear-gradient(90deg, #f97316, #ec4899, #8b5cf6)" }}
        >
          YouTube Car Reviews
        </span>
      </p>

      <div className="flex flex-wrap gap-2 mb-5">
        {deduped.slice(0, 8).map((c) => {
          const logo = makeLogoUrl(c.make);
          return (
            <Link
              key={`${c.make}-${c.model}`}
              href={`/videos?make=${encodeURIComponent(c.make)}&model=${encodeURIComponent(c.model)}`}
              className="flex items-center gap-1.5 rounded-full border border-border bg-card-soft px-3 py-1 text-sm text-fg-1 hover:border-acc transition-colors"
              style={{ fontWeight: 600, fontFamily: 'var(--font-sans)' }}
            >
              {logo && (
                <img src={logo} alt={c.make} className="h-3.5 w-3.5 inline-block rounded-sm object-contain" />
              )}
              <span>{c.make} {c.model}</span>
              <span className="rounded-full bg-card px-1.5 text-xs font-mono text-fg-3">
                {c.channel_count}
              </span>
            </Link>
          );
        })}
      </div>

      <Link
        href="/videos"
        className="text-sm text-acc hover:underline"
        style={{ fontWeight: 500 }}
      >
        Watch more car reviews →
      </Link>
    </div>
  );
}
